<!--
  Базовая кнопка для действий в интерфейсе.

  Как использовать:
  передайте текст кнопки через slot, а стандартные button-атрибуты через attrs:
  type, form, disabled, title и другие. Компонент сам прокинет их в настоящий
  <button> и добавит проектные стили.

  Пример обычной кнопки:
  <BaseButton
    variant="secondary"
    icon="edit"
    @click="startEditing"
  >
    Редактировать
  </BaseButton>

  Пример submit-кнопки для формы:
  <BaseButton
    variant="primary"
    type="submit"
    :form="formId"
    :disabled="isSubmitting"
  >
    {{ isSubmitting ? "Сохранение..." : "Сохранить" }}
  </BaseButton>

  Props:
  - variant: внешний вид кнопки. Доступны secondary, primary, danger, ghost.
    По умолчанию используется secondary.
  - icon: имя иконки из AppIcon. Если иконка нужна, передавайте её этим prop,
    не вставляйте AppIcon внутрь slot вручную.
  - right-icon: имя иконки справа от текста.
  - icon-size: размер иконки, по умолчанию 16.
  - size: normal или large. Large даёт мобильную высоту 52px.
  - compact: уменьшает кнопку для плотных панелей и таблиц.
  - icon-only: делает квадратную кнопку только с иконкой; передай aria-label.

  Disabled:
  обычное disabled-состояние передаётся как attr:
  <BaseButton :disabled="isLoading">Сохранить</BaseButton>.
  В этом случае кнопка просто блокируется и показывает свой обычный slot.

  Блокировка формы:
  если у кнопки есть attr form, BaseButton проверяет formBlockerStore для этой
  формы. Когда форма заблокирована, кнопка становится disabled автоматически.
  Если blocker передал message, BaseButton временно показывает этот текст
  вместо slot. Например AttachmentUploader может показать "Загрузка файлов".

  Loader в заблокированной форме:
  если blocker передал loading: true, рядом с message показывается маленький
  spinner. Это нужно для процессов вроде загрузки файлов, где пользователь
  должен видеть, что кнопка недоступна из-за активного действия, а не из-за
  ошибки в форме.

  Приоритет текста:
  локальный disabled не заменяет slot. Текст из formBlockerStore показывается
  только для блокировки формы, чтобы не ломать обычные кнопки со своим loading
  text вроде "Сохранение...".
-->
<template>
  <button
    class="btn"
    :class="buttonClasses"
    v-bind="buttonAttrs"
    @click="$emit('click', $event)"
  >
    <AppIcon
      v-if="icon && !buttonBlockMessage"
      class="btn__icon"
      :name="icon"
      :size="iconSize"
    />
    <span v-if="buttonBlockMessage" class="btn__block-content">
      <span
        v-if="buttonBlockIsLoading"
        class="btn__block-spinner"
      ></span>
      <span class="btn__block-message">
        {{ buttonBlockMessage }}
      </span>
    </span>
    <slot v-else />
    <AppIcon
      v-if="rightIcon && !buttonBlockMessage"
      class="btn__icon"
      :name="rightIcon"
      :size="iconSize"
    />
  </button>
</template>

<script type="text/javascript">
import AppIcon from "@/components/Icon/AppIcon.vue";

import { useFormBlockerStore } from "@/stores/formBlockerStore";

export default {
  name: "base-button",
  inheritAttrs: false,
  extends: {},
  components: {
    AppIcon,
  },
  props: {
    icon: {
      type: String,
      default: "",
    },
    rightIcon: {
      type: String,
      default: "",
    },
    iconSize: {
      type: [Number, String],
      default: 16,
    },
    variant: {
      type: String,
      default: "secondary",
      validator: (value) => [
        "secondary",
        "primary",
        "danger",
        "ghost",
      ].includes(value),
    },
    size: {
      type: String,
      default: "normal",
      validator: (value) => ["normal", "large"].includes(value),
    },
    compact: {
      type: Boolean,
      default: false,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click"],
  data() {
    return {};
  },
  computed: {
    formBlockerStore() {
      return useFormBlockerStore();
    },
    buttonAttrs() {
      return {
        ...this.$attrs,
        disabled: this.buttonIsDisabled,
      };
    },
    buttonIsDisabled() {
      return (
        this.attrIsDisabled ||
        this.formBlockerStore.formIsBlocked(this.$attrs.form)
      );
    },
    buttonBlockMessage() {
      if (this.attrIsDisabled) return "";

      return this.formBlockerStore.formBlockMessage(this.$attrs.form);
    },
    buttonBlockIsLoading() {
      return this.formBlockerStore.formBlockIsLoading(this.$attrs.form);
    },
    attrIsDisabled() {
      const disabled = this.$attrs.disabled;

      return disabled === "" || disabled === true;
    },
    buttonClasses() {
      return [
        `btn--${this.variant}`,
        `btn--${this.size}`,
        {
          "btn--with-icon": this.icon || this.rightIcon,
          "btn--compact": this.compact,
          "btn--icon-only": this.iconOnly,
        },
      ];
    },
  },
  watch: {},
  methods: {},
  beforeCreate() {},
  mounted() {},
};
</script>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  height: 32px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: 5px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 0.3s ease, color 0.2s linear;
}

.btn--with-icon {
  gap: 8px;
}

.btn--large {
  min-height: 52px;
  height: 52px;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 17px;
}

.btn--compact {
  min-height: 28px;
  height: 28px;
  padding: 0 10px;
  font-size: 13px;
}

.btn--icon-only {
  width: 32px;
  min-width: 32px;
  padding: 0;
}

.btn--compact.btn--icon-only {
  width: 28px;
  min-width: 28px;
}

.btn__icon {
  display: block;
  flex: 0 0 auto;
}

.btn__block-content {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.btn__block-spinner {
  flex: 0 0 auto;
  width: 13px;
  height: 13px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  opacity: 0.85;
  animation: btn-block-spin 0.8s linear infinite;
}

.btn__block-message {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes btn-block-spin {
  to {
    transform: rotate(360deg);
  }
}

.btn--secondary {
  border-color: #999;
  background-color: #fff;
  color: var(--color-dark);
}

.btn--secondary:hover:not(:disabled) {
  background-color: #e4e4e4;
}

.btn--primary {
  border-color: var(--color-green);
  background-color: var(--color-green);
  color: #fff;
}

.btn--primary:hover:not(:disabled) {
  border-color: var(--color-green-hover);
  background-color: var(--color-green-hover);
}

.btn--danger {
  border-color: var(--color-danger);
  background-color: #fff;
  color: var(--color-danger);
}

.btn--danger:hover:not(:disabled) {
  background-color: var(--color-danger);
  color: #fff;
}

.btn--ghost {
  border-color: transparent;
  background-color: transparent;
  color: var(--color-dark);
}

.btn--ghost:hover:not(:disabled) {
  background-color: #e4e4e4;
}

.btn:active:not(:disabled) {
  transform: translateY(2px);
}

.btn:disabled {
  cursor: not-allowed;
}

.btn--secondary:disabled,
.btn--ghost:disabled,
.btn--danger:disabled {
  background-color: #e4e4e4;
}

.btn--primary:disabled {
  border-color: #97c2bb;
  background-color: #97c2bb;
}

@media (max-width: 420px) {
  .btn {
    padding: 0 8px;
  }
}
</style>
